/** @jsxImportSource @emotion/react */
import type React from 'react';
import { useState } from 'react';
import { FiCheckCircle, FiCircle } from 'react-icons/fi';
import { FaSpinner } from 'react-icons/fa';
import {
  ErrorText,
  FieldGroup,
  FullWidth,
  Headline,
  PrimaryButton,
  Screen,
  Subhead,
  SuccessText,
} from './reset-password.styled';
import { useResetPassword } from './reset-password.hooks';
import { VSpace } from '../../chat/settings/account/account-settings';
import type { LoginStepProps } from '../login-shell.types';
import { SkipButton } from '../../registration/pages/secure-your-account/secure-your-account.styled';
import { FloatingInput } from '../../../components/floating-input/floating-input';
import { PasswordStrengthBar, calcScore } from '../../../components/strength-bar/strength-bar';

export const ResetPassword: React.FC<LoginStepProps> = ({ go, data }) => {
  const email = data.email || '';
  const resetCode = data.resetCode || '';

  const [triedSubmit, setTriedSubmit] = useState(false);

  const { pwd, setPwd, confirm, setConfirm, loading, error, success, submit } =
    useResetPassword({
      email,
      code: resetCode,
      onSuccess: () => {
        go('email-login');
      },
    });

  // Password rules
  const reqs = {
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /\d/.test(pwd),
    symbol: /[^A-Za-z0-9]/.test(pwd),
  };
  const checklistOk = Object.values(reqs).every(Boolean);

  const strengthScore = calcScore(pwd);
  const strongEnoughScore = strengthScore >= 10;

  const isTooWeak = pwd.length > 0 && (!checklistOk || !strongEnoughScore);
  const passwordsMatch = pwd.length > 0 && pwd === confirm;

  const isDisabled = loading || !passwordsMatch || isTooWeak || success;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTriedSubmit(true);

    if (isDisabled) return;
    await submit();
  };

  const inactiveColor = '#B7BDCBCC';

  return (
    <Screen data-testid="reset-password-screen">
      <div>

      <VSpace $h={32} />

      <Headline>Set a new password.</Headline>

      <VSpace $h={16} />

      <Subhead>Choose a new password for your account.</Subhead>

      <VSpace $h={32} />
      </div>

      <FullWidth>
        <FieldGroup>
          {/* CREATE PASSWORD */}
          <div style={{ position: 'relative' }}>
            <FloatingInput
              id="new-password"
              label="Create a password"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              showToggle
              data-testid="reset-pwd-new"
            />

            <div>
              <PasswordStrengthBar score={strengthScore} />
            </div>

            {/* checklist */}
            <ul
              css={{
                listStyle: 'none',
                padding: 0,
                margin: '8px 0 0',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              <Check ok={reqs.length} inactiveColor={inactiveColor}>
                8&nbsp;+ characters
              </Check>
              <Check ok={reqs.upper} inactiveColor={inactiveColor}>
                Upper-case letter
              </Check>
              <Check ok={reqs.lower} inactiveColor={inactiveColor}>
                Lower-case letter
              </Check>
              <Check ok={reqs.number} inactiveColor={inactiveColor}>
                Number
              </Check>
              <Check ok={reqs.symbol} inactiveColor={inactiveColor}>
                Symbol&nbsp;(!@#$…)
              </Check>
            </ul>
          </div>

          {/* CONFIRM PASSWORD */}
          <div style={{ position: 'relative' }}>
            <FloatingInput
              id="confirm-new-password"
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              showToggle
              data-testid="reset-pwd-confirm"
            />
          </div>
        </FieldGroup>

        {/* weak message */}
        {triedSubmit && isTooWeak && (
          <>
            <VSpace $h={8} />
            <ErrorText>
              Password too weak. Add upper/lower case letters, numbers and symbols.
            </ErrorText>
          </>
        )}

        {/* error */}
        {error && (
          <>
            <VSpace $h={8} />
            <ErrorText>{error}</ErrorText>
          </>
        )}

        {/* success */}
        {success && (
          <>
            <VSpace $h={8} />
            <SuccessText>Password reset successfully! Please log in.</SuccessText>
          </>
        )}

      <VSpace $h={24} />
        <PrimaryButton disabled={isDisabled} onClick={handleSubmit} data-testid="reset-pwd-submit">
          {loading ? <FaSpinner css={{ animation: 'spin 1s linear infinite' }} /> : 'Update Password'}
        </PrimaryButton>
  
      <VSpace $h={32} />
        <SkipButton
          onClick={() => go('email-login')}
          data-testid="reset-pwd-back"
          css={{
            color: '#B7BDCB',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontStyle: 'normal',
            fontSize: '16px',
            lineHeight: '100%',
            letterSpacing: '0px',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'pointer',
            height: 'auto',
          }}
        >
          Back to Login
        </SkipButton>
      </FullWidth>
    </Screen>
  );
};

const Check: React.FC<{ ok: boolean; inactiveColor?: string; children: React.ReactNode }> = ({
  ok,
  inactiveColor = '#B7BDCBCC',
  children,
}) => (
  <li
    css={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      paddingTop: 4,
      paddingLeft: 17,
    }}
  >
    <span
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#8097EA',
        lineHeight: 0,
      }}
    >
      {ok ? <FiCheckCircle size={14} /> : <FiCircle size={14} />}
    </span>
    <span
      css={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 12,
        fontWeight: ok ? 600 : 400,
        color: ok ? '#8097EA' : inactiveColor,
      }}
    >
      {children}
    </span>
  </li>
);
